import { createApi } from '@reduxjs/toolkit/query/react';
import customFetchBase from '@/app/common/utils/customFetchBase';
import { getBaseApiPath } from '@/app/common/api/config';
import {
  CreatePaymentRequest,
  CreatePaymentResponse,
  GetWalletBalanceRequest,
  GetWalletBalanceResponse,
  GetWalletBalanceApiData,
  GetWalletOrdersRequest,
  GetWalletOrdersResponse,
  GetWalletTransactionsRequest,
  GetWalletTransactionsResponse
} from '@/app/Wallet/types/walletApiTypes';
import { ApiResponse } from '@/app/common/types/common';
import { parseWalletBalance } from '@/app/Wallet/utils/walletBalance';

const walletApi = createApi({
  reducerPath: 'walletApi',
  baseQuery: (...args) => customFetchBase(getBaseApiPath(), ...args),
  tagTypes: ['Wallet'],
  endpoints: build => ({
    createPayment: build.mutation<ApiResponse<CreatePaymentResponse>, CreatePaymentRequest>({
      query: body => ({
        url: '/affiliate_price/v1/mizfatools/create-payment',
        method: 'POST',
        body
      }),
      invalidatesTags: ['Wallet']
    }),
    getWalletBalance: build.query<
      GetWalletBalanceResponse,
      GetWalletBalanceRequest
    >({
      query: body => ({
        url: '/affiliate_price/v1/mizfatools/wallet-balance-2',
        method: 'POST',
        body
      }),
      transformResponse: (response: ApiResponse<GetWalletBalanceApiData>) => {
        const personalBalance = parseWalletBalance(
          response.data?.personal_balance ?? response.data?.balance,
        );
        const aiBalance = parseWalletBalance(response.data?.ai_balance);
        return {
          balance: personalBalance,
          personalBalance,
          aiBalance,
        };
      },
      providesTags: ['Wallet']
    }),
    getWalletTransactions: build.mutation<
      ApiResponse<GetWalletTransactionsResponse>,
      GetWalletTransactionsRequest
    >({
      query: body => ({
        url: '/affiliate_price/v1/mizfatools/wallet-transactions',
        method: 'POST',
        body
      })
    }),
    getWalletTransactionsCount: build.query<GetWalletTransactionsResponse, GetWalletTransactionsRequest>({
      query: body => ({
        url: '/affiliate_price/v1/mizfatools/wallet-transactions',
        method: 'POST',
        body
      }),
      transformResponse: (response: ApiResponse<GetWalletTransactionsResponse>) => response.data
    }),
    getWalletOrdersList: build.query<GetWalletOrdersResponse, GetWalletOrdersRequest | void>({
      query: body => ({
        url: '/affiliate_price/v1/mizfatools/wallet-orders',
        method: 'POST',
        body: body ?? {}
      }),
      transformResponse: (response: ApiResponse<GetWalletOrdersResponse>) => response.data
    })
  })
});

export const {
  useCreatePaymentMutation,
  useGetWalletBalanceQuery,
  useGetWalletOrdersListQuery,
  useGetWalletTransactionsMutation,
  useGetWalletTransactionsCountQuery
} = walletApi;

export default walletApi;
